import { Box, Button, Layer, Spinner, Text } from 'grommet'
import parse from 'html-react-parser'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PrintIcon } from '../../../../assets/svgs'
import { ModalHeader } from '../../../../components/common'
import useLabResult from '../../../../hooks/useLabResults'
import { palette } from '../../../../themes/palette'
import {
  LabOrderIncludedLabResult,
  LabOrderIncludedPatient,
  LabOrderIncludedResource
} from '../../../../types/lab.type'
import {
  LabResult,
  LabResultIncluded,
  LabResultIncludedLabOrder,
  LabResultIncludedLabTest,
  LabResultIncludedOrder,
  LabResultIncludedStaff
} from '../../../../types/lab_results.type'
import LabResultDocumentPreview from '../../../LabTech/LabResultDocumentPreview'

// A comprehensive union type for all possible included resources
type EffectiveIncludedResource =
  | LabResultIncludedLabOrder
  | LabResultIncludedOrder
  | LabResultIncludedStaff
  | LabResultIncludedLabTest
  | LabOrderIncludedLabResult
  | LabOrderIncludedPatient

interface LabResultDetailsProps {
  isOpen: boolean
  onClose: () => void
  // Existing props for native lab results
  included?: LabResultIncluded[] // This is from useLabResult's response
  id?: string
  defaultData?: any

  // New props for patient lab results (from usePatientLabOrder)
  isPatientView?: boolean
  patientLabResultData?: LabOrderIncludedLabResult // The specific lab_result object from the included array
  patientIncluded?: LabOrderIncludedResource[] // The entire included array from usePatientLabOrder
}

const tabs = ['Results', 'Details']

const LabResultDetails = ({
  isOpen,
  onClose,
  included: nativeIncluded = [],
  id,
  defaultData,
  isPatientView = false,
  patientLabResultData,
  patientIncluded = []
}: LabResultDetailsProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') || tabs[0]
  const [activeTab, setActiveTab] = useState(tab)
  const handleTabChange = (val: string) => {
    setActiveTab(val)
    setSearchParams(prev => {
      prev.set('tab', val)
      return prev
    })
  }

  const labResultId = id ?? defaultData?.id
  const { labResult: fetchedLabResult, isLabResultLoading } = useLabResult('_', labResultId)

  const labResultToDisplay = isPatientView ? patientLabResultData : fetchedLabResult?.data || defaultData

  const effectiveIncluded: EffectiveIncludedResource[] = isPatientView
    ? (patientIncluded as EffectiveIncludedResource[])
    : (fetchedLabResult?.included as EffectiveIncludedResource[]) || (nativeIncluded as EffectiveIncludedResource[])

  if (!isOpen) return null

  // Only show loading/error for the native flow where data is fetched by the modal itself
  if (!isPatientView) {
    if (isLabResultLoading) {
      return (
        <Layer margin={{ vertical: '4px' }} modal onClickOutside={onClose} onEsc={onClose} position="right">
          <Box fill style={{ height: '100vh', width: 'min(500px, calc(100vw - 1.5rem))' }}>
            <ModalHeader onClose={onClose} title="Lab Results" />
            <Box align="center" fill justify="center">
              <Spinner size="medium" />
              <Text margin={{ top: 'small' }}>Loading lab result...</Text>
            </Box>
          </Box>
        </Layer>
      )
    }
  }

  if (!labResultToDisplay) {
    return (
      <Layer margin={{ vertical: '4px' }} modal onClickOutside={onClose} onEsc={onClose} position="right">
        <Box fill style={{ height: '100vh', width: 'min(500px, calc(100vw - 1.5rem))' }}>
          <ModalHeader onClose={onClose} title="Lab Results" />
          <Box align="center" fill justify="center">
            <Text color="status-error">No lab result data available to display.</Text>
          </Box>
        </Box>
      </Layer>
    )
  }

  // Helper: find included by type and id
  const findIncluded = <T extends EffectiveIncludedResource>(type: string, findId: string): T | undefined => {
    return (effectiveIncluded as any[])?.find(inc => inc.type === type && inc?.id === findId) as T | undefined
  }

  const orderBundleId =
    labResultToDisplay.relationships?.lab_order?.data?.id ?? labResultToDisplay.relationships?.order?.data?.id

  const labOrder = orderBundleId ? findIncluded<LabResultIncludedLabOrder>('lab_order', orderBundleId) : undefined
  const order = orderBundleId ? findIncluded<LabResultIncludedOrder>('order', orderBundleId) : undefined

  const labTest =
    order && order.relationships?.orderable?.data?.id
      ? findIncluded<LabResultIncludedLabTest>('lab_test', order.relationships.orderable.data.id)
      : undefined

  const resultTitle =
    labResultToDisplay.attributes?.document?.name ??
    labTest?.attributes?.name ??
    labOrder?.attributes?.test_names?.[0] ??
    '-'

  const interpretationLabel =
    labResultToDisplay.attributes?.interpretation != null && labResultToDisplay.attributes?.interpretation !== ''
      ? parse(labResultToDisplay.attributes.interpretation)
      : labResultToDisplay.attributes?.document?.name
        ? labResultToDisplay.attributes.document.name
        : '-'

  const interpretationStatus =
    labResultToDisplay.attributes?.interpretation?.trim() || labResultToDisplay.attributes?.document?.name || '-'

  // Get staff for 'Order by', 'Reviewed by', 'Performed by'
  // Order by: order.relationships.ordered_by.data?.id
  let orderedByName = '-'
  if (order && order.relationships?.ordered_by?.data) {
    const staff = findIncluded<LabResultIncludedStaff>('staff', order.relationships.ordered_by.data.id)
    if (staff) {
      orderedByName = `${staff.attributes.first_name} ${staff.attributes.last_name}`
    }
  } else if (labOrder?.relationships?.ordered_by && 'data' in (labOrder.relationships.ordered_by || {})) {
    const ob = labOrder.relationships.ordered_by as { data?: { id?: string } }
    const sid = ob.data?.id
    if (sid) {
      const staff = findIncluded<LabResultIncludedStaff>('staff', sid)
      if (staff) {
        orderedByName = `${staff.attributes.first_name} ${staff.attributes.last_name}`
      }
    }
  }

  // Performed by: labResult.data.relationships.studied_by.data?.id
  let performedByName = '-'
  if (labResultToDisplay.relationships?.studied_by && labResultToDisplay.relationships?.studied_by?.data) {
    const staff = findIncluded<LabResultIncludedStaff>('staff', labResultToDisplay.relationships.studied_by.data.id)
    if (staff) {
      performedByName = `${staff.attributes.first_name} ${staff.attributes.last_name}`
    }
  }

  // Reviewed by: (assuming same as performed by for now, unless another field is available)
  const reviewedByName = performedByName

  const handlePrint = () => {
    window.print()
  }

  // Details for the Details section
  const details = {
    Date:
      order?.attributes.created_at != null
        ? new Date(order.attributes.created_at).toLocaleDateString()
        : labOrder?.attributes.created_at != null
          ? new Date(labOrder.attributes.created_at).toLocaleDateString()
          : '-',
    Lab: labTest?.attributes.category || '-',
    'Order by': orderedByName, // This will be '-' if no order/labOrder is found
    'Performed Date': labResultToDisplay.attributes?.created_at
      ? new Date(labResultToDisplay.attributes.created_at).toLocaleDateString()
      : '-',
    'Performed by': performedByName, // This will be '-' if no staff is found
    'Reference Number': orderBundleId ?? '-',
    'Reviewed Date': labResultToDisplay.attributes?.updated_at
      ? new Date(labResultToDisplay.attributes.updated_at).toLocaleDateString()
      : '-',
    'Reviewed by': reviewedByName // This will be '-' if no staff is found
  }

  return (
    <Layer margin={{ vertical: '4px' }} modal onClickOutside={onClose} onEsc={onClose} position="right" style={{}}>
      <Box fill style={{ height: '100vh', width: 'min(500px, calc(100vw - 1.5rem))' }}>
        <ModalHeader onClose={onClose} title="Details" />
        <Box overflow={'auto'} pad={'medium'}>
          <Box direction="column" gap={'1em'} margin={{ bottom: '2.5em' }} pad={{ horizontal: '20px' }}>
            <Box align="center" direction="row" gap={'1em'}>
              <Text color="black" size="24px" weight={'bolder'}>
                {resultTitle}
              </Text>

              {/* <Badge justify="center" label={interpretationLabel} size="small" status={interpretationStatus} /> */}
            </Box>
            <Box direction="row" margin={{ top: '.4em' }}>
              <Button
                className="!border !rounded-[10px] !border-[#E6E6E6] hover:!bg-[#E6E6E6] "
                color="#fff"
                icon={<PrintIcon />}
                label={'Print'}
                margin={{ right: 'small' }}
                onClick={handlePrint}
                pad={{ horizontal: '10px' }}
                secondary
                style={{ color: 'black', fontSize: '14px', fontWeight: 'normal' }}
                type="button"
              />
              {/* <Button
                className="!border !rounded-[10px] !border-[#E6E6E6] hover:!bg-[#E6E6E6] "
                color="#fff"
                icon={<DownloadIcon />}
                label="Download"
                onClick={() => {}}
                pad={{ horizontal: '10px' }}
                secondary
                style={{ color: 'black', fontSize: '14px', fontWeight: 'normal' }}
                type="button"
              /> */}
            </Box>
          </Box>
          <Box className="border-b border-b-[#D9D9D9] " direction={'row'} gap={'2px'}>
            {tabs?.map(tab => (
              <Box
                border={{ color: activeTab === tab ? palette.greenKelp : 'transparent', side: 'bottom', size: '1px' }}
                className={`tab ${activeTab === tab ? 'active' : ''}`}
                key={tab}
                onClick={() => handleTabChange(tab)}
                pad={{ horizontal: '20px', vertical: '10px' }}
              >
                {tab}
              </Box>
            ))}
          </Box>
          <Box flex pad={'20px'}>
            {activeTab === 'Details' && (
              <Box gap={'14px'} margin={{ bottom: '24px' }}>
                {Object.entries(details)?.map(([key, value]) => (
                  <Box direction="row" gap={'18px'} justify="between" key={key}>
                    <Text color="#666666" size="16px">
                      {key}:
                    </Text>
                    <Text color={'black'} size="14px">
                      {value}
                    </Text>
                  </Box>
                ))}
              </Box>
            )}
            {activeTab === 'Results' && (
              <Box gap={'14px'} margin={{ bottom: '24px' }}>
                {labResultToDisplay.attributes?.document ? (
                  <LabResultDocumentPreview document={labResultToDisplay.attributes.document} />
                ) : (
                  (labResultToDisplay as LabResult).attributes?.components?.map((comp, idx) => (
                    <Box direction="row" gap={'18px'} justify="between" key={idx}>
                      <Text className="capitalize" weight={500}>
                        {comp.name}:
                      </Text>
                      <Text>
                        {comp?.value} {comp.unit}
                      </Text>
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Layer>
  )
}

export default LabResultDetails
